import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Skeleton, Chip, Grid } from '@mui/material';
import { green, red, blue, grey } from '@mui/material/colors';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NoBackpackIcon from '@mui/icons-material/HelpOutline';
import api from '../../../services/api';

const fmt = (iso) => iso
    ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '--';

const fmtDur = (sec) => {
    if (sec == null) return '--';
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m}min`;
    const h = Math.floor(m / 60);
    return `${h}h${String(m % 60).padStart(2, '0')}`;
};

export default function FooterOps() {
    const [cycle, setCycle] = useState(null);
    const [orphans, setOrphans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/hermes/dashboard/last_cycle').then(r => r.data).catch(() => null),
            api.get('/hermes/dashboard/orphan_hosts').then(r => r.data).catch(() => []),
        ]).then(([c, o]) => {
            setCycle(c);
            setOrphans(o);
        }).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={140} /></Grid>
                <Grid item xs={12} md={6}><Skeleton variant="rectangular" height={140} /></Grid>
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <DoneAllIcon sx={{ color: green[600] }} />
                        <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily: 'Montserrat' }}>
                            Último ciclo
                        </Typography>
                    </Box>
                    {cycle?.started_at ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography variant="body2">
                                <strong>Início:</strong> {fmt(cycle.started_at)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Fim:</strong> {fmt(cycle.ended_at)}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Duração:</strong> {fmtDur(cycle.duration_seconds)} · {cycle.hosts_processed} hosts
                            </Typography>
                            <Typography variant="caption" color="text.secondary" mt={0.5}>
                                Agendado pra {fmt(cycle.cycle_scheduled_at)}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Ainda não houve backups no ciclo atual.
                        </Typography>
                    )}
                </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <NoBackpackIcon sx={{ color: orphans.length ? red[600] : grey[500] }} />
                        <Typography variant="subtitle1" fontWeight={600} sx={{ fontFamily: 'Montserrat' }}>
                            Hosts órfãos
                        </Typography>
                        <Chip label={orphans.length} size="small"
                              color={orphans.length ? 'error' : 'default'} sx={{ ml: 0.5 }} />
                    </Box>
                    {orphans.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Todo host ativo já teve pelo menos um backup.
                        </Typography>
                    ) : (
                        <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} sx={{ overflowY: 'auto' }}>
                            {orphans.slice(0, 6).map((o) => (
                                <Box key={o.host_id} display="flex" justifyContent="space-between" alignItems="center"
                                     sx={{ py: 0.25, '&:hover': { bgcolor: grey[50] } }}>
                                    <Typography variant="body2" noWrap>
                                        {o.host_name}
                                        <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                                            {o.ip}
                                        </Typography>
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: blue[700], flexShrink: 0, ml: 1 }}>
                                        {o.days_orphan}d sem backup nenhum
                                    </Typography>
                                </Box>
                            ))}
                            {orphans.length > 6 && (
                                <Typography variant="caption" color="text.secondary" mt={0.5}>
                                    + {orphans.length - 6} hosts órfãos
                                </Typography>
                            )}
                        </Box>
                    )}
                </Paper>
            </Grid>
        </Grid>
    );
}
